const FLOW_VERSION = 2;

const REVIEW_QUESTION_TYPES = {
  RATING_QUESTION: "rating",
  SINGLE_CHOICE_QUESTION: "single_choice",
  OPINION_SCALE_QUESTION: "opinion_scale",
  RANKING_QUESTION: "ranking",
  TEXTAREA_QUESTION: "textarea",
  LANDING_PAGE: "landing_page",
};

const REVIEW_FLOW_TARGET_TYPES = {
  TARGET_QUESTION: "question",
  TARGET_END: "end",
};

const REVIEW_CONDITION_OPERATORS = {
  OPERATOR_EQ: "eq",
  OPERATOR_NEQ: "neq",
  OPERATOR_LT: "lt",
  OPERATOR_LTE: "lte",
  OPERATOR_GT: "gt",
  OPERATOR_GTE: "gte",
  OPERATOR_IS: "is",
  OPERATOR_IS_NOT: "is_not",
  OPERATOR_HAS: "has",
  OPERATOR_RANKING_FIRST_IS: "ranking_first_is",
  OPERATOR_RANKING_FIRST_IS_NOT: "ranking_first_is_not",
  OPERATOR_RANKING_BEFORE: "ranking_before",
  OPERATOR_RANKING_AFTER: "ranking_after",
};

export const REVIEW_FLOW_CONDITION_KINDS = {
  ANSWER: "answer",
  HAS_UPSELLING: "has_upselling",
};

const RANKING_RELATION_SEPARATOR = "::";

const QUESTION_TYPE_ALIASES = {
  rating_question: REVIEW_QUESTION_TYPES.RATING_QUESTION,
  [REVIEW_QUESTION_TYPES.RATING_QUESTION]: REVIEW_QUESTION_TYPES.RATING_QUESTION,
  single_choice_question: REVIEW_QUESTION_TYPES.SINGLE_CHOICE_QUESTION,
  [REVIEW_QUESTION_TYPES.SINGLE_CHOICE_QUESTION]: REVIEW_QUESTION_TYPES.SINGLE_CHOICE_QUESTION,
  opinion_scale_question: REVIEW_QUESTION_TYPES.OPINION_SCALE_QUESTION,
  [REVIEW_QUESTION_TYPES.OPINION_SCALE_QUESTION]: REVIEW_QUESTION_TYPES.OPINION_SCALE_QUESTION,
  ranking_question: REVIEW_QUESTION_TYPES.RANKING_QUESTION,
  [REVIEW_QUESTION_TYPES.RANKING_QUESTION]: REVIEW_QUESTION_TYPES.RANKING_QUESTION,
  textarea_question: REVIEW_QUESTION_TYPES.TEXTAREA_QUESTION,
  [REVIEW_QUESTION_TYPES.TEXTAREA_QUESTION]: REVIEW_QUESTION_TYPES.TEXTAREA_QUESTION,
  landing_page_question: REVIEW_QUESTION_TYPES.LANDING_PAGE,
  [REVIEW_QUESTION_TYPES.LANDING_PAGE]: REVIEW_QUESTION_TYPES.LANDING_PAGE,
};

const parseJsonSafely = (value, fallback) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  return value;
};

const SIMPLE_REVIEW_CONFIG_KEY = "__simpleReviewConfig";
const SIMPLE_REVIEW_QUESTIONS_KEY = "__questions";
export const SIMPLE_REVIEW_FONT_FAMILIES = {
  modern: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  rounded: '"Trebuchet MS", "Segoe UI", sans-serif',
  classic: 'Arial, Helvetica, sans-serif',
  elegant: 'Georgia, "Times New Roman", serif',
};
export const SIMPLE_REVIEW_DESIGN_DEFAULTS = {
  cardBackgroundColor: "#FFFFFF",
  textColor: "#111111",
  accentColor: "#D4A72C",
  accentMutedColor: "#D1D5DB",
  fontFamily: "modern",
  textAlign: "center",
  titleFontSize: 24,
  messageFontSize: 15,
  ratingSize: 48,
  cardWidth: 360,
  logoSize: 68,
  cardBorderRadius: 16,
  showHotelLogo: true,
};

const extractSimpleReviewPayload = (rawQuestions) => {
  const parsed = parseJsonSafely(rawQuestions, null);
  if (
    parsed &&
    typeof parsed === "object" &&
    !Array.isArray(parsed) &&
    parsed[SIMPLE_REVIEW_QUESTIONS_KEY] &&
    typeof parsed[SIMPLE_REVIEW_QUESTIONS_KEY] === "object" &&
    !Array.isArray(parsed[SIMPLE_REVIEW_QUESTIONS_KEY])
  ) {
    return {
      config:
        parsed[SIMPLE_REVIEW_CONFIG_KEY] &&
        typeof parsed[SIMPLE_REVIEW_CONFIG_KEY] === "object" &&
        !Array.isArray(parsed[SIMPLE_REVIEW_CONFIG_KEY])
          ? parsed[SIMPLE_REVIEW_CONFIG_KEY]
          : {},
      questions: parsed[SIMPLE_REVIEW_QUESTIONS_KEY],
    };
  }

  return {
    config: {},
    questions: parsed,
  };
};

const LANDING_PAGE_DEFAULT_STYLES = {
  backgroundColor: "#FFFFFF",
  textColor: "#000000",
  buttonBackgroundColor: "#888888",
  buttonTextColor: "#FFFFFF",
};

export const REVIEW_FLOW_THEME_DEFAULTS = {
  backgroundColor: "#FFFFFF",
  textColor: "#888888",
  buttonBackgroundColor: "#888888",
  buttonTextColor: "#FFFFFF",
  fontFamily: "default",
  titleFontSize: 28,
  descriptionFontSize: 16,
  buttonLabel: "Continuar",
};

const normalizeHexColor = (value, fallback) => {
  const color = String(value || "").trim();
  return /^#(?:[0-9A-F]{3}){1,2}$/i.test(color) ? color.toUpperCase() : fallback;
};

const normalizeBoolean = (value, fallback) => {
  if (typeof value === "boolean") {
    return value;
  }
  return fallback;
};

const normalizeNumber = (value, fallback, min, max) => {
  const nextValue = Number(value);
  if (!Number.isFinite(nextValue)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, Math.round(nextValue)));
};

const normalizeOptionalNumber = (value, fallback = null, min = 0, max = Number.MAX_SAFE_INTEGER) => {
  if (value === null || value === undefined || `${value}`.trim() === "") {
    return fallback;
  }
  const nextValue = Number(value);
  if (!Number.isFinite(nextValue)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, Math.round(nextValue)));
};

const normalizeTextAlign = (value, fallback = "left") => {
  return ["left", "center", "right"].includes(value) ? value : fallback;
};

export const REVIEW_FLOW_FONT_FAMILIES = {
  default: 'Arial, Helvetica, sans-serif',
  modern: '"Open Sans", sans-serif',
  rounded: '"Poppins", sans-serif',
  classic: '"Banana Grotesk", sans-serif',
  elegant: '"Raleway", sans-serif',
};

export const REVIEW_FLOW_LAYOUT_PRESETS = {
  AUTO: "auto",
  QUICK_ANSWER: "quick_answer",
  BALANCED: "balanced",
  QUESTION_FOCUS: "question_focus",
  IMAGE_FOCUS: "image_focus",
};

export const REVIEW_FLOW_MOBILE_CONTENT_POSITIONS = {
  TOP: "top",
  CENTER: "center",
  BOTTOM: "bottom",
};

export const getReviewFlowNodeAutoAdvanceDefaults = (node) => {
  switch (node?.type) {
    case REVIEW_QUESTION_TYPES.OPINION_SCALE_QUESTION:
    case REVIEW_QUESTION_TYPES.RATING_QUESTION:
      return { enabled: true, delayMs: 200 };
    case REVIEW_QUESTION_TYPES.SINGLE_CHOICE_QUESTION:
      return { enabled: true, delayMs: 200 };
    case REVIEW_QUESTION_TYPES.RANKING_QUESTION:
    case REVIEW_QUESTION_TYPES.TEXTAREA_QUESTION:
    default:
      return { enabled: false, delayMs: 200 };
  }
};

const normalizeFontFamily = (value, fallback = "default") => {
  return Object.prototype.hasOwnProperty.call(REVIEW_FLOW_FONT_FAMILIES, value) ? value : fallback;
};

const normalizeLayoutPreset = (value, fallback = REVIEW_FLOW_LAYOUT_PRESETS.AUTO) => {
  return Object.values(REVIEW_FLOW_LAYOUT_PRESETS).includes(value) ? value : fallback;
};

const normalizeMobileContentPosition = (value, fallback = REVIEW_FLOW_MOBILE_CONTENT_POSITIONS.CENTER) => {
  return Object.values(REVIEW_FLOW_MOBILE_CONTENT_POSITIONS).includes(value) ? value : fallback;
};

const normalizeOptionalBoolean = (value, fallback = null) => {
  return typeof value === "boolean" ? value : fallback;
};

const normalizeSimpleReviewDesign = (design) => ({
  cardBackgroundColor: normalizeHexColor(
    design?.cardBackgroundColor,
    SIMPLE_REVIEW_DESIGN_DEFAULTS.cardBackgroundColor
  ),
  textColor: normalizeHexColor(design?.textColor, SIMPLE_REVIEW_DESIGN_DEFAULTS.textColor),
  accentColor: normalizeHexColor(design?.accentColor, SIMPLE_REVIEW_DESIGN_DEFAULTS.accentColor),
  accentMutedColor: normalizeHexColor(
    design?.accentMutedColor,
    SIMPLE_REVIEW_DESIGN_DEFAULTS.accentMutedColor
  ),
  fontFamily: Object.prototype.hasOwnProperty.call(SIMPLE_REVIEW_FONT_FAMILIES, design?.fontFamily)
    ? design.fontFamily
    : SIMPLE_REVIEW_DESIGN_DEFAULTS.fontFamily,
  textAlign: design?.textAlign === "left" ? "left" : SIMPLE_REVIEW_DESIGN_DEFAULTS.textAlign,
  titleFontSize: normalizeNumber(design?.titleFontSize, SIMPLE_REVIEW_DESIGN_DEFAULTS.titleFontSize, 18, 40),
  messageFontSize: normalizeNumber(design?.messageFontSize, SIMPLE_REVIEW_DESIGN_DEFAULTS.messageFontSize, 12, 24),
  ratingSize: normalizeNumber(design?.ratingSize, SIMPLE_REVIEW_DESIGN_DEFAULTS.ratingSize, 32, 72),
  cardWidth: normalizeNumber(design?.cardWidth, SIMPLE_REVIEW_DESIGN_DEFAULTS.cardWidth, 280, 420),
  logoSize: normalizeNumber(design?.logoSize, SIMPLE_REVIEW_DESIGN_DEFAULTS.logoSize, 36, 120),
  cardBorderRadius: normalizeNumber(
    design?.cardBorderRadius,
    SIMPLE_REVIEW_DESIGN_DEFAULTS.cardBorderRadius,
    0,
    32
  ),
  showHotelLogo: normalizeBoolean(design?.showHotelLogo, SIMPLE_REVIEW_DESIGN_DEFAULTS.showHotelLogo),
});

export const getSimpleReviewConfig = (rawQuestions) => {
  const config = extractSimpleReviewPayload(rawQuestions).config || {};
  return {
    ...config,
    heroImage: `${config?.heroImage || ""}`.trim(),
    design: normalizeSimpleReviewDesign(config?.design),
  };
};

const firstNonEmptyFromI18n = (i18n, fallback = "") => {
  if (!i18n || typeof i18n !== "object") {
    return fallback;
  }
  const preferred = String(i18n.default || i18n["en-US"] || "").trim();
  if (preferred) {
    return preferred;
  }
  return Object.values(i18n).find((value) => String(value || "").trim()) || fallback;
};

export const normalizeReviewFlowTheme = (theme) => {
  const buttonLabelI18n = normalizeI18nValue(theme?.buttonLabelI18n);
  return {
    backgroundColor: normalizeHexColor(theme?.backgroundColor, REVIEW_FLOW_THEME_DEFAULTS.backgroundColor),
    textColor: normalizeHexColor(theme?.textColor, REVIEW_FLOW_THEME_DEFAULTS.textColor),
    buttonBackgroundColor: normalizeHexColor(
      theme?.buttonBackgroundColor,
      REVIEW_FLOW_THEME_DEFAULTS.buttonBackgroundColor
    ),
    buttonTextColor: normalizeHexColor(theme?.buttonTextColor, REVIEW_FLOW_THEME_DEFAULTS.buttonTextColor),
    fontFamily: normalizeFontFamily(theme?.fontFamily, REVIEW_FLOW_THEME_DEFAULTS.fontFamily),
    titleFontSize: normalizeNumber(theme?.titleFontSize, REVIEW_FLOW_THEME_DEFAULTS.titleFontSize, 14, 56),
    descriptionFontSize: normalizeNumber(
      theme?.descriptionFontSize,
      REVIEW_FLOW_THEME_DEFAULTS.descriptionFontSize,
      12,
      32
    ),
    buttonLabel:
      String(firstNonEmptyFromI18n(buttonLabelI18n, theme?.buttonLabel || REVIEW_FLOW_THEME_DEFAULTS.buttonLabel)).trim() ||
      REVIEW_FLOW_THEME_DEFAULTS.buttonLabel,
    buttonLabelI18n,
  };
};

export const getReviewFlowTheme = (reviewFlow) => normalizeReviewFlowTheme(reviewFlow?.theme);

const hasLegacyNodeDesignOverrides = (node) =>
  !!(
    node &&
    typeof node === "object" &&
    (Object.prototype.hasOwnProperty.call(node, "backgroundColor") ||
      Object.prototype.hasOwnProperty.call(node, "textColor") ||
      Object.prototype.hasOwnProperty.call(node, "buttonBackgroundColor") ||
      Object.prototype.hasOwnProperty.call(node, "buttonTextColor") ||
      (node?.fontFamily && node.fontFamily !== "default") ||
      (node?.titleFontSize !== null && node?.titleFontSize !== undefined) ||
      (node?.descriptionFontSize !== null && node?.descriptionFontSize !== undefined) ||
      ((node?.type !== REVIEW_QUESTION_TYPES.LANDING_PAGE ||
        String(node?.buttonLabel || "").trim() !== REVIEW_FLOW_THEME_DEFAULTS.buttonLabel) &&
        (String(node?.buttonLabel || "").trim() ||
          Object.values(parseJsonSafely(node?.buttonLabelI18n, {}) || {}).some((value) =>
            String(value || "").trim()
          ))))
  );

export const resolveReviewFlowNodeTheme = (reviewFlow, node) => {
  const flowTheme = getReviewFlowTheme(reviewFlow);
  if (!node || !node.useCustomDesign) {
    return flowTheme;
  }
  const buttonLabelI18n = normalizeI18nValue(node?.buttonLabelI18n);
  return {
    backgroundColor: normalizeHexColor(node.backgroundColor, flowTheme.backgroundColor),
    textColor: normalizeHexColor(node.textColor, flowTheme.textColor),
    buttonBackgroundColor: normalizeHexColor(node.buttonBackgroundColor, flowTheme.buttonBackgroundColor),
    buttonTextColor: normalizeHexColor(node.buttonTextColor, flowTheme.buttonTextColor),
    fontFamily: normalizeFontFamily(node?.fontFamily, flowTheme.fontFamily),
    titleFontSize: normalizeOptionalNumber(node?.titleFontSize, flowTheme.titleFontSize, 14, 56),
    descriptionFontSize: normalizeOptionalNumber(
      node?.descriptionFontSize,
      flowTheme.descriptionFontSize,
      12,
      32
    ),
    buttonLabel:
      String(firstNonEmptyFromI18n(buttonLabelI18n, node?.buttonLabel || flowTheme.buttonLabel)).trim() ||
      flowTheme.buttonLabel,
    buttonLabelI18n,
  };
};

const normalizeI18nValue = (value) => {
  const parsed = parseJsonSafely(value, {});
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
};

const normalizeQuestionType = (type) => {
  return QUESTION_TYPE_ALIASES[type] || REVIEW_QUESTION_TYPES.RATING_QUESTION;
};

const normalizeActivationCondition = (condition) => {
  const kind =
    condition?.kind === REVIEW_FLOW_CONDITION_KINDS.HAS_UPSELLING
      ? REVIEW_FLOW_CONDITION_KINDS.HAS_UPSELLING
      : REVIEW_FLOW_CONDITION_KINDS.ANSWER;
  return {
    id: condition?.id || "",
    kind,
    nodeId:
      kind === REVIEW_FLOW_CONDITION_KINDS.ANSWER ? condition?.nodeId || condition?.questionId || "" : "",
    operator:
      kind === REVIEW_FLOW_CONDITION_KINDS.HAS_UPSELLING
        ? REVIEW_CONDITION_OPERATORS.OPERATOR_HAS
        : condition?.operator || condition?.comparator || REVIEW_CONDITION_OPERATORS.OPERATOR_IS,
    value: condition?.value ?? "",
  };
};

const normalizeActivation = (activation) => {
  if (!activation || typeof activation !== "object") {
    return {
      mode: "always",
      operator: "all",
      conditions: [],
    };
  }

  const rawConditions = activation.conditions || activation.rules || [];
  return {
    mode: activation.mode === "conditional" ? "conditional" : "always",
    operator: activation.operator === "any" ? "any" : "all",
    conditions: Array.isArray(rawConditions) ? rawConditions.map(normalizeActivationCondition) : [],
  };
};

const normalizeTarget = (target) => {
  if (!target || typeof target !== "object") {
    return {
      type: REVIEW_FLOW_TARGET_TYPES.TARGET_END,
      targetNodeId: "",
    };
  }

  return {
    type:
      target.type === REVIEW_FLOW_TARGET_TYPES.TARGET_QUESTION
        ? REVIEW_FLOW_TARGET_TYPES.TARGET_QUESTION
        : REVIEW_FLOW_TARGET_TYPES.TARGET_END,
    targetNodeId:
      target.type === REVIEW_FLOW_TARGET_TYPES.TARGET_QUESTION ? target.targetNodeId || "" : "",
  };
};

const normalizeBlock = (block) => {
  const type = normalizeQuestionType(block?.type);
  return {
    id: block?.id || "",
    enabled: block?.enabled !== false,
    type,
    title: block?.title || block?.question || "",
    titleI18n: normalizeI18nValue(block?.titleI18n || block?.questionI18n),
    description: block?.description || "",
    descriptionI18n: normalizeI18nValue(block?.descriptionI18n),
    required: type === REVIEW_QUESTION_TYPES.LANDING_PAGE ? false : !!block?.required,
    scaleMin: Number(block?.scaleMin ?? (type === REVIEW_QUESTION_TYPES.OPINION_SCALE_QUESTION ? 0 : 1)),
    scaleMax: Number(block?.scaleMax ?? (type === REVIEW_QUESTION_TYPES.OPINION_SCALE_QUESTION ? 10 : 5)),
    lowLabel: block?.lowLabel || "",
    lowLabelI18n: normalizeI18nValue(block?.lowLabelI18n),
    highLabel: block?.highLabel || "",
    highLabelI18n: normalizeI18nValue(block?.highLabelI18n),
    maxSelections: Number(block?.maxSelections || 1),
    image: block?.image || "",
    mobileImageMinHeight: normalizeNumber(block?.mobileImageMinHeight, 220, 120, 420),
    textAlign: normalizeTextAlign(block?.textAlign, "left"),
    fontFamily: normalizeFontFamily(block?.fontFamily, "default"),
    layoutPreset: normalizeLayoutPreset(block?.layoutPreset, REVIEW_FLOW_LAYOUT_PRESETS.AUTO),
    mobileContentPosition: normalizeMobileContentPosition(
      block?.mobileContentPosition,
      REVIEW_FLOW_MOBILE_CONTENT_POSITIONS.CENTER
    ),
    autoAdvanceEnabled: normalizeOptionalBoolean(block?.autoAdvanceEnabled, null),
    autoAdvanceDelayMs: 400,
    titleFontSize: normalizeOptionalNumber(block?.titleFontSize, null, 14, 56),
    descriptionFontSize: normalizeOptionalNumber(block?.descriptionFontSize, null, 12, 32),
    placeholder: block?.placeholder || "",
    placeholderI18n: normalizeI18nValue(block?.placeholderI18n),
    buttonLabel: block?.buttonLabel || "",
    buttonLabelI18n: normalizeI18nValue(block?.buttonLabelI18n),
    headerBlockMinHeight: normalizeOptionalNumber(block?.headerBlockMinHeight, null, 60, 420),
    answerBlockMinHeight: normalizeOptionalNumber(block?.answerBlockMinHeight, null, 80, 520),
    estimatedDuration: block?.estimatedDuration || "",
    estimatedDurationI18n: normalizeI18nValue(block?.estimatedDurationI18n),
    useCustomDesign:
      typeof block?.useCustomDesign === "boolean" ? block.useCustomDesign : hasLegacyNodeDesignOverrides(block),
    backgroundColor: normalizeHexColor(
      block?.backgroundColor,
      LANDING_PAGE_DEFAULT_STYLES.backgroundColor
    ),
    textColor: normalizeHexColor(block?.textColor, LANDING_PAGE_DEFAULT_STYLES.textColor),
    buttonBackgroundColor: normalizeHexColor(
      block?.buttonBackgroundColor,
      LANDING_PAGE_DEFAULT_STYLES.buttonBackgroundColor
    ),
    buttonTextColor: normalizeHexColor(
      block?.buttonTextColor,
      LANDING_PAGE_DEFAULT_STYLES.buttonTextColor
    ),
    options:
      type === REVIEW_QUESTION_TYPES.SINGLE_CHOICE_QUESTION || type === REVIEW_QUESTION_TYPES.RANKING_QUESTION
        ? (Array.isArray(block?.options) ? block.options : []).map((option) => ({
            id: option?.id || "",
            label: option?.label || "",
            labelI18n: normalizeI18nValue(option?.labelI18n),
          }))
        : [],
    activation: normalizeActivation(block?.activation || block?.visibility),
  };
};

const createLinearFallbacks = (nodes = []) => {
  return nodes.map((node, index) => ({
    sourceNodeId: node.id,
    then:
      index < nodes.length - 1
        ? {
            type: REVIEW_FLOW_TARGET_TYPES.TARGET_QUESTION,
            targetNodeId: nodes[index + 1].id,
          }
        : { type: REVIEW_FLOW_TARGET_TYPES.TARGET_END, targetNodeId: "" },
  }));
};

const normalizeRuleConditionKind = (condition) =>
  condition?.kind === REVIEW_FLOW_CONDITION_KINDS.HAS_UPSELLING
    ? REVIEW_FLOW_CONDITION_KINDS.HAS_UPSELLING
    : REVIEW_FLOW_CONDITION_KINDS.ANSWER;

const isReviewFlowUpsellingCondition = (condition) =>
  normalizeRuleConditionKind(condition) === REVIEW_FLOW_CONDITION_KINDS.HAS_UPSELLING;

const buildComputedMaps = (flow) => {
  const nodeById = flow.nodes.reduce((acc, node) => {
    acc[node.id] = node;
    return acc;
  }, {});

  const rulesBySourceId = flow.flow.rules.reduce((acc, rule) => {
    if (!acc[rule.sourceNodeId]) {
      acc[rule.sourceNodeId] = [];
    }
    acc[rule.sourceNodeId].push(rule);
    return acc;
  }, {});

  const fallbackBySourceId = flow.flow.fallbacks.reduce((acc, fallback) => {
    acc[fallback.sourceNodeId] = fallback;
    return acc;
  }, {});

  return {
    ...flow,
    nodeById,
    rulesBySourceId,
    fallbackBySourceId,
  };
};

const normalizeVersionedFlow = (parsed) => {
  const nodes = Array.isArray(parsed?.nodes) ? parsed.nodes.map(normalizeBlock) : [];
  const fallbackMap = {};
  const initialFallbacks = Array.isArray(parsed?.flow?.fallbacks) ? parsed.flow.fallbacks : [];

  initialFallbacks.forEach((fallback) => {
    fallbackMap[fallback.sourceNodeId] = {
      sourceNodeId: fallback?.sourceNodeId || "",
      then: normalizeTarget(fallback?.then),
    };
  });

  createLinearFallbacks(nodes).forEach((fallback) => {
    if (!fallbackMap[fallback.sourceNodeId]) {
      fallbackMap[fallback.sourceNodeId] = fallback;
    }
  });

  return buildComputedMaps({
    version: FLOW_VERSION,
    startNodeId: nodes.some((node) => node.id === parsed?.startNodeId) ? parsed.startNodeId : nodes[0]?.id || "",
    theme: normalizeReviewFlowTheme(parsed?.theme),
    nodes,
    flow: {
      rules: Array.isArray(parsed?.flow?.rules)
        ? parsed.flow.rules.map((rule) => ({
            id: rule?.id || "",
            sourceNodeId: rule?.sourceNodeId || "",
            conditions: Array.isArray(rule?.conditions)
              ? rule.conditions.map((condition, index) => ({
                  id: condition?.id || "",
                  join: index === 0 ? null : condition?.join === "or" ? "or" : "and",
                  kind: normalizeRuleConditionKind(condition),
                  nodeId:
                    normalizeRuleConditionKind(condition) === REVIEW_FLOW_CONDITION_KINDS.ANSWER
                      ? condition?.nodeId || ""
                      : "",
                  operator:
                    normalizeRuleConditionKind(condition) === REVIEW_FLOW_CONDITION_KINDS.HAS_UPSELLING
                      ? REVIEW_CONDITION_OPERATORS.OPERATOR_HAS
                      : condition?.operator || REVIEW_CONDITION_OPERATORS.OPERATOR_EQ,
                  value: condition?.value ?? "",
                }))
              : [],
            then: normalizeTarget(rule?.then),
          }))
        : [],
      fallbacks: Object.values(fallbackMap),
    },
  });
};

const migrateLegacyQuestionsToFlow = (parsed) => {
  const nodes = parsed && typeof parsed === "object" && !Array.isArray(parsed)
    ? Object.keys(parsed).map((questionId) =>
        normalizeBlock({
          id: questionId,
          type: REVIEW_QUESTION_TYPES.RATING_QUESTION,
          question: parsed[questionId]?.question || "",
          questionI18n: parsed[questionId]?.questionI18n,
          description: parsed[questionId]?.description || "",
          descriptionI18n: parsed[questionId]?.descriptionI18n,
        })
      )
    : [];

  return normalizeVersionedFlow({
    version: FLOW_VERSION,
    startNodeId: nodes[0]?.id || "",
    nodes,
    flow: {
      rules: [],
      fallbacks: createLinearFallbacks(nodes),
    },
  });
};

const migrateIntermediateBlocksToFlow = (blocks) => {
  const nodes = (Array.isArray(blocks) ? blocks : []).map((block) =>
    normalizeBlock({
      ...block,
      activation: block?.visibility,
    })
  );

  return normalizeVersionedFlow({
    version: FLOW_VERSION,
    startNodeId: nodes[0]?.id || "",
    nodes,
    flow: {
      rules: [],
      fallbacks: createLinearFallbacks(nodes),
    },
  });
};

export const normalizeReviewFlow = (rawQuestions) => {
  const parsed = extractSimpleReviewPayload(rawQuestions).questions;

  if (parsed?.version === FLOW_VERSION || Array.isArray(parsed?.nodes)) {
    return normalizeVersionedFlow(parsed);
  }

  if (Array.isArray(parsed)) {
    return migrateIntermediateBlocksToFlow(parsed);
  }

  return migrateLegacyQuestionsToFlow(parsed);
};

export const normalizeReviewQuestionBlocks = (rawQuestions) => {
  return normalizeReviewFlow(rawQuestions).nodes;
};

export const parseReviewAnswers = (rawAnswers) => {
  const parsed = parseJsonSafely(rawAnswers, {});
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
};

export const getReviewQuestionOption = (block, optionId) => {
  return (block?.options || []).find((option) => option.id === optionId) || null;
};

const parseRankingRelationValue = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return {
      leftOptionId: `${value.leftOptionId || value.left || ""}`.trim(),
      rightOptionId: `${value.rightOptionId || value.right || ""}`.trim(),
    };
  }

  const raw = `${value ?? ""}`;
  const [leftOptionId = "", rightOptionId = ""] = raw.split(RANKING_RELATION_SEPARATOR);
  return {
    leftOptionId: leftOptionId.trim(),
    rightOptionId: rightOptionId.trim(),
  };
};

const evaluateOperator = (operator, answer, expectedValue) => {
  const left = typeof answer === "number" ? answer : Number(answer);
  const right = typeof expectedValue === "number" ? expectedValue : Number(expectedValue);

  if (
    [
      REVIEW_CONDITION_OPERATORS.OPERATOR_EQ,
      REVIEW_CONDITION_OPERATORS.OPERATOR_NEQ,
      REVIEW_CONDITION_OPERATORS.OPERATOR_LT,
      REVIEW_CONDITION_OPERATORS.OPERATOR_LTE,
      REVIEW_CONDITION_OPERATORS.OPERATOR_GT,
      REVIEW_CONDITION_OPERATORS.OPERATOR_GTE,
    ].includes(operator)
  ) {
    if (Number.isNaN(left) || Number.isNaN(right)) {
      return false;
    }
  }

  switch (operator) {
    case REVIEW_CONDITION_OPERATORS.OPERATOR_EQ:
      return left === right;
    case REVIEW_CONDITION_OPERATORS.OPERATOR_NEQ:
      return left !== right;
    case REVIEW_CONDITION_OPERATORS.OPERATOR_LT:
      return left < right;
    case REVIEW_CONDITION_OPERATORS.OPERATOR_LTE:
      return left <= right;
    case REVIEW_CONDITION_OPERATORS.OPERATOR_GT:
      return left > right;
    case REVIEW_CONDITION_OPERATORS.OPERATOR_GTE:
      return left >= right;
    case REVIEW_CONDITION_OPERATORS.OPERATOR_IS:
      return Array.isArray(answer)
        ? `${answer[0] ?? ""}` === `${expectedValue ?? ""}`
        : `${answer ?? ""}` === `${expectedValue ?? ""}`;
    case REVIEW_CONDITION_OPERATORS.OPERATOR_IS_NOT:
      return Array.isArray(answer)
        ? `${answer[0] ?? ""}` !== `${expectedValue ?? ""}`
        : `${answer ?? ""}` !== `${expectedValue ?? ""}`;
    case REVIEW_CONDITION_OPERATORS.OPERATOR_RANKING_FIRST_IS:
      return Array.isArray(answer) && `${answer[0] ?? ""}` === `${expectedValue ?? ""}`;
    case REVIEW_CONDITION_OPERATORS.OPERATOR_RANKING_FIRST_IS_NOT:
      return Array.isArray(answer) && `${answer[0] ?? ""}` !== `${expectedValue ?? ""}`;
    case REVIEW_CONDITION_OPERATORS.OPERATOR_RANKING_BEFORE: {
      if (!Array.isArray(answer)) {
        return false;
      }
      const { leftOptionId, rightOptionId } = parseRankingRelationValue(expectedValue);
      const leftIndex = answer.findIndex((optionId) => `${optionId}` === leftOptionId);
      const rightIndex = answer.findIndex((optionId) => `${optionId}` === rightOptionId);
      return leftIndex >= 0 && rightIndex >= 0 && leftIndex < rightIndex;
    }
    case REVIEW_CONDITION_OPERATORS.OPERATOR_RANKING_AFTER: {
      if (!Array.isArray(answer)) {
        return false;
      }
      const { leftOptionId, rightOptionId } = parseRankingRelationValue(expectedValue);
      const leftIndex = answer.findIndex((optionId) => `${optionId}` === leftOptionId);
      const rightIndex = answer.findIndex((optionId) => `${optionId}` === rightOptionId);
      return leftIndex >= 0 && rightIndex >= 0 && leftIndex > rightIndex;
    }
    default:
      return false;
  }
};

const buildUpsellingIdSet = (context = {}) => {
  const items = Array.isArray(context?.crossUpSelling) ? context.crossUpSelling : [];
  return new Set(
    items.flatMap((item) =>
      [item?.id, item?.crossUpSellingId, item?.catalogUpsellingId, item?.optionId].filter(
        (value) => `${value || ""}`.trim() !== ""
      )
    )
  );
};

const evaluateReviewCondition = (condition, answers = {}, context = {}) => {
  if (isReviewFlowUpsellingCondition(condition)) {
    if (condition.operator !== REVIEW_CONDITION_OPERATORS.OPERATOR_HAS) {
      return false;
    }
    return buildUpsellingIdSet(context).has(`${condition.value ?? ""}`);
  }

  return evaluateOperator(condition.operator, answers?.[condition.nodeId], condition.value);
};

export const evaluateReviewActivation = (activation, answers = {}, context = {}) => {
  const normalizedActivation = normalizeActivation(activation);
  if (normalizedActivation.mode !== "conditional") {
    return true;
  }

  if ((normalizedActivation.conditions || []).length === 0) {
    return true;
  }

  const matches = normalizedActivation.conditions.map((condition) =>
    evaluateReviewCondition(condition, answers, context)
  );

  return normalizedActivation.operator === "any" ? matches.some(Boolean) : matches.every(Boolean);
};

export const evaluateReviewRule = (rule, answers = {}, context = {}) => {
  const conditions = Array.isArray(rule?.conditions) ? rule.conditions : [];
  if (conditions.length === 0) {
    return false;
  }

  return conditions.reduce((result, condition, index) => {
    const nextMatch = evaluateReviewCondition(condition, answers, context);
    if (index === 0) {
      return nextMatch;
    }
    return condition.join === "or" ? result || nextMatch : result && nextMatch;
  }, false);
};

const getReviewRuleConditionSpecificity = (operator) => {
  switch (operator) {
    case REVIEW_CONDITION_OPERATORS.OPERATOR_EQ:
    case REVIEW_CONDITION_OPERATORS.OPERATOR_IS:
    case REVIEW_CONDITION_OPERATORS.OPERATOR_RANKING_FIRST_IS:
    case REVIEW_CONDITION_OPERATORS.OPERATOR_RANKING_BEFORE:
    case REVIEW_CONDITION_OPERATORS.OPERATOR_RANKING_AFTER:
      return 100;
    case REVIEW_CONDITION_OPERATORS.OPERATOR_LT:
    case REVIEW_CONDITION_OPERATORS.OPERATOR_LTE:
    case REVIEW_CONDITION_OPERATORS.OPERATOR_GT:
    case REVIEW_CONDITION_OPERATORS.OPERATOR_GTE:
      return 10;
    case REVIEW_CONDITION_OPERATORS.OPERATOR_NEQ:
    case REVIEW_CONDITION_OPERATORS.OPERATOR_IS_NOT:
    case REVIEW_CONDITION_OPERATORS.OPERATOR_RANKING_FIRST_IS_NOT:
      return 1;
    default:
      return 0;
  }
};

const getReviewRuleSpecificity = (rule) => {
  const conditions = Array.isArray(rule?.conditions) ? rule.conditions : [];
  return conditions.reduce(
    (acc, condition) => acc + getReviewRuleConditionSpecificity(condition?.operator),
    conditions.length * 20
  );
};

/** Valores alineados con `ReviewSettingTypeEnum` en GraphQL (`simple` | `flow`). */
export const REVIEW_SETTING_TYPES = {
  SIMPLE: "simple",
  FLOW: "flow",
};

const isReviewFlowNodeEnabled = (node) => node?.enabled !== false;

const getReviewFlowTargetNodeId = (target) =>
  target?.type === REVIEW_FLOW_TARGET_TYPES.TARGET_QUESTION ? target.targetNodeId || "" : "";

const getReviewFlowFallbackTargetNodeId = (reviewFlow, nodeId) =>
  getReviewFlowTargetNodeId(reviewFlow?.fallbackBySourceId?.[nodeId]?.then);

const getReviewFlowMainChainFromNormalizedFlow = (normalizedFlow) => {
  const chain = [];
  const visited = new Set();
  let currentNodeId = normalizedFlow.startNodeId || "";

  while (currentNodeId && !visited.has(currentNodeId) && normalizedFlow.nodeById?.[currentNodeId]) {
    chain.push(currentNodeId);
    visited.add(currentNodeId);
    currentNodeId = getReviewFlowFallbackTargetNodeId(normalizedFlow, currentNodeId);
  }

  return chain;
};

/** Reglas condicionales en el JSON guardado (editor de flujo). */
const questionsIndicateConditionalReviewFlow = (rawQuestions) => {
  const parsed = extractSimpleReviewPayload(rawQuestions).questions;
  const rules = parsed?.flow?.rules;
  return Array.isArray(rules) && rules.length > 0;
};

/**
 * Varias preguntas "principales" en cadena (secciones padre) → UX de flujo sin paso de estrellas globales.
 */
const questionsIndicateMultiPrincipalFlow = (rawQuestions) => {
  const parsed = extractSimpleReviewPayload(rawQuestions).questions;
  if (!parsed || Number(parsed.version) !== FLOW_VERSION) {
    return false;
  }
  const nf = normalizeReviewFlow(rawQuestions);
  const chain = getReviewFlowMainChainFromNormalizedFlow(nf);
  return chain.length > 1;
};

const questionsJsonIndicatesFlowMode = (rawQuestions) =>
  questionsIndicateConditionalReviewFlow(rawQuestions) || questionsIndicateMultiPrincipalFlow(rawQuestions);

const questionsJsonIndicatesSimpleMode = (rawQuestions) => {
  const parsed = parseJsonSafely(rawQuestions, null);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return false;
  }

  if (
    parsed[SIMPLE_REVIEW_QUESTIONS_KEY] &&
    typeof parsed[SIMPLE_REVIEW_QUESTIONS_KEY] === "object" &&
    !Array.isArray(parsed[SIMPLE_REVIEW_QUESTIONS_KEY])
  ) {
    return true;
  }

  return !parsed.version && !parsed.nodes && !parsed.flow;
};

/**
 * Normaliza el tipo del setting para la PWA.
 * Retrocompat: `review` (nombre antiguo) → simple; sin valor o desconocido → flow.
 * `simple` con JSON de flujo (reglas o varias principales) → flow.
 */
export const getEffectiveReviewSettingType = (reviewSetting) => {
  const t = String(reviewSetting?.type ?? "").trim().toLowerCase();
  const fromJson = questionsJsonIndicatesFlowMode(reviewSetting?.questions);
  const simpleFromJson = questionsJsonIndicatesSimpleMode(reviewSetting?.questions);

  if (t === "flow") {
    return REVIEW_SETTING_TYPES.FLOW;
  }
  if (t === "simple" || t === "review") {
    if (fromJson) {
      return REVIEW_SETTING_TYPES.FLOW;
    }
    return REVIEW_SETTING_TYPES.SIMPLE;
  }
  if (simpleFromJson && !fromJson) {
    return REVIEW_SETTING_TYPES.SIMPLE;
  }
  return REVIEW_SETTING_TYPES.FLOW;
};

/**
 * Agrupa cada pregunta principal (cadena start → fallbacks) con los nodos alcanzables
 * solo por reglas y fallbacks sin “salir” a otra principal de la cadena principal.
 * Refleja cómo el hotelero enlaza condicionales bajo cada bloque principal en el editor.
 */
const deriveReviewFlowSectionsFromNormalizedFlow = (normalizedFlow) => {
  const nodes = normalizedFlow.nodes || [];
  const nodeById = normalizedFlow.nodeById || {};
  const rulesBySourceId = normalizedFlow.rulesBySourceId || {};
  const mainChain = getReviewFlowMainChainFromNormalizedFlow(normalizedFlow);
  const mainSet = new Set(mainChain);
  const assigned = new Set(mainChain);
  const conditionalNodeIdsByPrincipal = Object.fromEntries(mainChain.map((principalId) => [principalId, []]));
  const queue = [];

  mainChain.forEach((principalId) => {
    (rulesBySourceId[principalId] || []).forEach((rule) => {
      const targetNodeId = getReviewFlowTargetNodeId(rule.then);
      if (targetNodeId) {
        queue.push({ principalId, nodeId: targetNodeId });
      }
    });
  });

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current?.nodeId || !nodeById[current.nodeId] || mainSet.has(current.nodeId) || assigned.has(current.nodeId)) {
      continue;
    }

    assigned.add(current.nodeId);
    conditionalNodeIdsByPrincipal[current.principalId].push(current.nodeId);

    (rulesBySourceId[current.nodeId] || []).forEach((rule) => {
      const targetNodeId = getReviewFlowTargetNodeId(rule.then);
      if (targetNodeId) {
        queue.push({ principalId: current.principalId, nodeId: targetNodeId });
      }
    });

    const fallbackTargetNodeId = getReviewFlowFallbackTargetNodeId(normalizedFlow, current.nodeId);
    if (fallbackTargetNodeId) {
      queue.push({ principalId: current.principalId, nodeId: fallbackTargetNodeId });
    }
  }

  const sections = mainChain.map((principalId) => ({
    principalId,
    conditionalNodeIds: conditionalNodeIdsByPrincipal[principalId] || [],
    isDetached: false,
  }));

  nodes.forEach((node) => {
    if (!assigned.has(node.id)) {
      sections.push({
        principalId: node.id,
        conditionalNodeIds: [],
        isDetached: true,
      });
    }
  });

  return sections;
};

const getReviewFlowPrincipalOwnerIdFromNormalizedFlow = (normalizedFlow, nodeId) => {
  if (!nodeId) {
    return "";
  }

  const sections = deriveReviewFlowSectionsFromNormalizedFlow(normalizedFlow);
  const owner = sections.find(
    (section) => section.principalId === nodeId || (section.conditionalNodeIds || []).includes(nodeId)
  );
  return owner?.principalId || "";
};

const canEnterReviewFlowNode = (normalizedFlow, nodeId, answers, context) => {
  const node = normalizedFlow.nodeById?.[nodeId];
  if (!node || !isReviewFlowNodeEnabled(node)) {
    return false;
  }

  const ownerId = getReviewFlowPrincipalOwnerIdFromNormalizedFlow(normalizedFlow, nodeId);
  if (ownerId) {
    const principalNode = normalizedFlow.nodeById?.[ownerId];
    if (principalNode && !evaluateReviewActivation(principalNode.activation, answers, context)) {
      return false;
    }
  }

  if (ownerId === nodeId) {
    return true;
  }

  return evaluateReviewActivation(node.activation, answers, context);
};

/**
 * Siguiente nodo en la misma sección (orden BFS del grafo guardado) con activación satisfecha,
 * antes de pasar a otra principal o de cerrar el tramo (end).
 */
const resolveNextInSamePrincipalSection = (normalizedFlow, currentNodeId, answers = {}, context = {}) => {
  const ownerPrincipalId = getReviewFlowPrincipalOwnerIdFromNormalizedFlow(normalizedFlow, currentNodeId);
  if (!ownerPrincipalId) {
    return null;
  }

  const sections = deriveReviewFlowSectionsFromNormalizedFlow(normalizedFlow);
  const section = sections.find((s) => s.principalId === ownerPrincipalId);
  if (!section || section.isDetached) {
    return null;
  }

  const ordered = [section.principalId, ...(section.conditionalNodeIds || [])];
  const idx = ordered.indexOf(currentNodeId);
  if (idx === -1) {
    return null;
  }

  for (let j = idx + 1; j < ordered.length; j += 1) {
    const nid = ordered[j];
    const node = normalizedFlow.nodeById?.[nid];
    if (!node || !isReviewFlowNodeEnabled(node)) {
      continue;
    }
    if (!canEnterReviewFlowNode(normalizedFlow, nid, answers, context)) {
      continue;
    }
    return nid;
  }

  return null;
};

/** Tras endFlow / fin de subgrafo bajo la principal actual: siguiente principal activa en la cadena. */
const resolveNextPrincipalAfterCurrentSection = (normalizedFlow, currentNodeId, answers = {}, context = {}) => {
  const ownerPrincipalId = getReviewFlowPrincipalOwnerIdFromNormalizedFlow(normalizedFlow, currentNodeId);
  if (!ownerPrincipalId) {
    return null;
  }

  const chain = getReviewFlowMainChainFromNormalizedFlow(normalizedFlow);
  const ownerIndex = chain.indexOf(ownerPrincipalId);
  if (ownerIndex === -1) {
    return null;
  }

  for (let j = ownerIndex + 1; j < chain.length; j += 1) {
    const principalId = chain[j];
    const node = normalizedFlow.nodeById?.[principalId];
    if (!node || !isReviewFlowNodeEnabled(node)) {
      continue;
    }
    const entry = resolveTargetNodeId(
      normalizedFlow,
      { type: REVIEW_FLOW_TARGET_TYPES.TARGET_QUESTION, targetNodeId: principalId },
      answers,
      context
    );
    if (entry) {
      return entry;
    }
  }

  return null;
};

const resolveTargetNodeId = (reviewFlow, target, answers = {}, context = {}, visited = new Set()) => {
  const normalizedTarget = normalizeTarget(target);
  if (normalizedTarget.type === REVIEW_FLOW_TARGET_TYPES.TARGET_END) {
    return null;
  }

  if (!normalizedTarget.targetNodeId || visited.has(normalizedTarget.targetNodeId)) {
    return null;
  }

  const node = reviewFlow.nodeById?.[normalizedTarget.targetNodeId];
  if (!node) {
    return null;
  }

  if (canEnterReviewFlowNode(reviewFlow, node.id, answers, context)) {
    return node.id;
  }

  const nextFallback = reviewFlow.fallbackBySourceId?.[node.id];
  if (!nextFallback) {
    return null;
  }

  const nextVisited = new Set(visited);
  nextVisited.add(node.id);
  return resolveTargetNodeId(reviewFlow, nextFallback.then, answers, context, nextVisited);
};

export const resolveReviewFlowStartNodeId = (reviewFlow, answers = {}, context = {}) => {
  const normalizedFlow = normalizeReviewFlow(reviewFlow);
  return resolveTargetNodeId(
    normalizedFlow,
    {
      type: REVIEW_FLOW_TARGET_TYPES.TARGET_QUESTION,
      targetNodeId: normalizedFlow.startNodeId,
    },
    answers,
    context
  );
};

/** Primera pregunta visible del flujo: entrada de la primera principal habilitada en la cadena (no un hijo condicional). */
export const resolveFirstMainPrincipalEntryNodeId = (reviewFlow, answers = {}, context = {}) => {
  const normalizedFlow = normalizeReviewFlow(reviewFlow);
  const chain = getReviewFlowMainChainFromNormalizedFlow(normalizedFlow);
  for (let i = 0; i < chain.length; i += 1) {
    const principalId = chain[i];
    const entry = resolveTargetNodeId(
      normalizedFlow,
      { type: REVIEW_FLOW_TARGET_TYPES.TARGET_QUESTION, targetNodeId: principalId },
      answers,
      context
    );
    if (entry) {
      return entry;
    }
  }
  return null;
};

export const resolveReviewFlowNextNodeId = (reviewFlow, currentNodeId, answers = {}, options = {}) => {
  const normalizedFlow = normalizeReviewFlow(reviewFlow);
  const sourceRules = normalizedFlow.rulesBySourceId?.[currentNodeId] || [];
  const context = options.context || {};

  const matchedRules = sourceRules
    .map((rule, index) => ({ rule, index }))
    .filter(({ rule }) => evaluateReviewRule(rule, answers, context))
    .sort((a, b) => {
      const specificityDiff = getReviewRuleSpecificity(b.rule) - getReviewRuleSpecificity(a.rule);
      if (specificityDiff !== 0) {
        return specificityDiff;
      }
      return a.index - b.index;
    });

  for (const { rule } of matchedRules) {
    const fromRule = resolveTargetNodeId(normalizedFlow, rule.then, answers, context);
    if (fromRule) {
      return fromRule;
    }
  }

  const fallback = normalizedFlow.fallbackBySourceId?.[currentNodeId];
  const reviewSettingType = options.reviewSettingType ?? REVIEW_SETTING_TYPES.FLOW;

  const fromFallback = resolveTargetNodeId(
    normalizedFlow,
    fallback?.then || { type: REVIEW_FLOW_TARGET_TYPES.TARGET_END, targetNodeId: "" },
    answers,
    context
  );
  if (fromFallback) {
    return fromFallback;
  }

  const isSimpleMode =
    reviewSettingType === REVIEW_SETTING_TYPES.SIMPLE || reviewSettingType === "review";
  if (isSimpleMode) {
    return null;
  }

  return resolveNextPrincipalAfterCurrentSection(normalizedFlow, currentNodeId, answers, context);
};

/** Claves `review.flow_type_*` en locales PWA; `translate` es típicamente `T` de react-translator-component. */
const REVIEW_QUESTION_TYPE_TITLE_KEYS = {
  [REVIEW_QUESTION_TYPES.RATING_QUESTION]: "review.flow_type_rating",
  [REVIEW_QUESTION_TYPES.SINGLE_CHOICE_QUESTION]: "review.flow_type_single_choice",
  [REVIEW_QUESTION_TYPES.OPINION_SCALE_QUESTION]: "review.flow_type_opinion_scale",
  [REVIEW_QUESTION_TYPES.RANKING_QUESTION]: "review.flow_type_ranking",
  [REVIEW_QUESTION_TYPES.LANDING_PAGE]: "review.flow_type_landing",
};

export const formatReviewQuestionTypeTitle = (translate, type) => {
  if (typeof translate !== "function") {
    return String(type || "");
  }
  const canonical = normalizeQuestionType(type);
  const key = REVIEW_QUESTION_TYPE_TITLE_KEYS[canonical];
  return key ? translate(key) : String(type || "");
};

export { REVIEW_QUESTION_TYPES, REVIEW_FLOW_TARGET_TYPES, REVIEW_CONDITION_OPERATORS };
